import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, MessageSquareHeart, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const QUESTIONS = [
  { key: "q1", text: "How easy is it to generate a receipt?", options: ["Very easy", "Okay", "Difficult"] },
  { key: "q2", text: "Is WhatsApp sharing to tenants working smoothly?", options: ["Yes", "Sometimes", "No"] },
  { key: "q3", text: "How useful is the bulk tenant upload?", options: ["Very useful", "Okay", "Not useful"] },
  { key: "q4", text: "Are the pending payment reminders helpful?", options: ["Very helpful", "Somewhat", "Not helpful"] },
  { key: "q5", text: "Would you recommend PGRentReceipts to other PG/Hostel owners?", options: ["Definitely", "Maybe", "Not yet"] },
];

export default function FeedbackTab() {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!rating) return toast.error("Please select a star rating");
    const missing = QUESTIONS.filter((q) => !answers[q.key]);
    if (missing.length) return toast.error("Please answer all questions");
    setBusy(true);
    try {
      await api.post("/feedback", { rating, answers, comments });
      setDone(true);
      toast.success("Thank you for your feedback!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit feedback");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Card className="p-10 border-slate-200 max-w-2xl text-center" data-testid="feedback-thank-you">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
        <h2 className="font-display text-2xl font-semibold mt-4">Thank you for your feedback!</h2>
        <p className="text-sm text-slate-500 mt-2">Your input helps us make PGRentReceipts better for every PG and Hostel owner.</p>
        <div className="flex justify-center gap-1 mt-4">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`h-6 w-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          ))}
        </div>
        <Button variant="outline" className="mt-6" onClick={() => { setDone(false); setRating(0); setAnswers({}); setComments(""); }} data-testid="feedback-another-button">
          Submit another response
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-slate-200 max-w-2xl" data-testid="feedback-tab">
      <div className="mb-5">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><MessageSquareHeart className="h-5 w-5 text-indigo-600" /> Feedback</h2>
        <p className="text-sm text-slate-500 mt-0.5">Tell us how PGRentReceipts is working for you — it takes less than a minute.</p>
      </div>

      {/* Questions */}
      <div className="space-y-5">
        {QUESTIONS.map((q, qi) => (
          <div key={q.key} data-testid={`feedback-question-${qi + 1}`}>
            <p className="text-sm font-medium text-slate-800 mb-2">{qi + 1}. {q.text}</p>
            <div className="flex flex-wrap gap-2">
              {q.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setAnswers((a) => ({ ...a, [q.key]: opt }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    answers[q.key] === opt
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400 hover:text-indigo-700"
                  }`}
                  data-testid={`feedback-q${qi + 1}-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Star rating */}
      <div className="mt-6 mb-6">
        <p className="text-sm font-medium text-slate-800 mb-2">Overall, how would you rate your experience?</p>
        <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              className="p-0.5 transition-transform hover:scale-110"
              data-testid={`feedback-star-${n}`}
            >
              <Star className={`h-8 w-8 transition-colors ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
          <span className="ml-2 text-sm text-slate-500" data-testid="feedback-rating-label">
            {rating > 0 ? `${rating} Star${rating > 1 ? "s" : ""}` : "Tap a star"}
          </span>
        </div>
      </div>

      {/* Comments */}
      <div className="mt-6">
        <p className="text-sm font-medium text-slate-800 mb-2">Anything else you'd like to share? (optional)</p>
        <Textarea
          rows={3}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Suggestions, feature requests, or issues you faced…"
          data-testid="feedback-comments-input"
        />
      </div>

      <div className="pt-5 flex justify-end">
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={busy} data-testid="feedback-submit-button">
          {busy ? "Submitting…" : "Submit feedback"}
        </Button>
      </div>
    </Card>
  );
}
